import { useState } from 'react'
import type { RefObject } from 'react'
import type { Quote_ } from 'src/api/db/apiQuote'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { useRequestContext } from '../../../hooks/RequestContext'
import { downloadInvoicePdf } from '../downloadInvoicePdf'
import { buildIssuedInvoiceData, getBillingDocumentData } from '../utils'
import { EditInvoiceDialog } from './EditInvoiceDialog'
import { SendInvoiceDialog } from './SendInvoiceDialog'

export const InvoiceActions = ({
  quote,
  documentRef,
}: {
  quote: Quote_
  documentRef: RefObject<HTMLElement | null>
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const isAdmin = useIsAdmin()
  const { refetchQuote } = useRequestContext()
  const { updateQuote } = useQuoteApi({ skipListQuery: true })
  const [editOpen, setEditOpen] = useState(false)
  const [sendOpen, setSendOpen] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const invoice = getBillingDocumentData(quote)

  const handleCreateInvoice = async () => {
    try {
      const invoiceData = buildIssuedInvoiceData(quote)
      const result = await updateQuote.mutateAsync({
        id: quote.id,
        invoice_data: invoiceData,
      })

      if (result.error) throw result.error

      refetchQuote()
      toast({
        title: t('Invoice created', 'Faktura skapad'),
        description: invoiceData.invoiceNumber,
      })
    } catch (err) {
      console.error('Failed to create invoice', err)
      toast({
        variant: 'destructive',
        title: t('Could not create invoice', 'Kunde inte skapa faktura'),
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }

  const handleDownloadPdf = async () => {
    const element = documentRef.current
    if (!element) {
      toast({
        variant: 'destructive',
        title: t('Could not download PDF', 'Kunde inte ladda ner PDF'),
      })
      return
    }

    setIsDownloading(true)
    try {
      await downloadInvoicePdf(element, `${invoice.invoiceNumber}.pdf`)
      toast({
        title: t('PDF downloaded', 'PDF nedladdad'),
        description: invoice.invoiceNumber,
      })
    } catch (err) {
      console.error('Failed to download invoice PDF', err)
      toast({
        variant: 'destructive',
        title: t('Could not download PDF', 'Kunde inte ladda ner PDF'),
        description: err instanceof Error ? err.message : undefined,
      })
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Column className="items-start gap-1">
      <Row className="flex-wrap justify-start gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleDownloadPdf}
          disabled={isDownloading}
        >
          {isDownloading
            ? t('Downloading...', 'Laddar ner...')
            : t('Download PDF', 'Ladda ner PDF')}
        </Button>
        {isAdmin && (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditOpen(true)}
            >
              {t('Edit invoice', 'Redigera faktura')}
            </Button>
            {invoice.isDraftPreview ? (
              <Button
                size="sm"
                onClick={handleCreateInvoice}
                disabled={updateQuote.isPending}
              >
                {updateQuote.isPending
                  ? t('Creating...', 'Skapar...')
                  : t('Create invoice', 'Skapa faktura')}
              </Button>
            ) : null}
          </>
        )}
        <Button size="sm" onClick={() => setSendOpen(true)}>
          {t('Send invoice', 'Skicka faktura')}
        </Button>
      </Row>

      {isAdmin && (
        <EditInvoiceDialog
          quote={quote}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
      <SendInvoiceDialog
        quote={quote}
        open={sendOpen}
        onOpenChange={setSendOpen}
      />
    </Column>
  )
}
